<template>
  <v-row>
    <v-col>
      <v-row align="center">
        <v-col>
          <h3 class="subtitle-1">Available Documents</h3>
        </v-col>
        <v-col>
          <v-row justify="end">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  class="mr-3 mb-3"
                  v-on="on"
                  small
                  fab
                  dark
                  color="primary"
                  @click="onCreateDocument"
                >
                  <v-icon dark>add</v-icon>
                </v-btn>
              </template>
              <span>Create new Document Type</span>
            </v-tooltip>
          </v-row>
        </v-col>
      </v-row>
      <v-card outlined>
        <v-data-table :items="allDocs" :headers="allDocsHeaders">
          <template v-slot:item.actions="{ item }">
            <v-row>
              <v-btn icon small class="mx-0" @click="onAvailDocDetails(item)">
                <v-icon small color="cyan">mdi-file-find</v-icon>
              </v-btn>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    small
                    class="mx-0"
                    @click="onAddDocument(item)"
                    v-on="on"
                  >
                    <v-icon small color="green">mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Add Document</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    small
                    class="mx-0"
                    @click="onCloneAvailDoc(item)"
                  >
                    <v-icon color="primary" small>mdi-content-copy</v-icon>
                  </v-btn>
                </template>
                <span>Copy</span>
              </v-tooltip>
              <confirmation-dialog @onConfirm="onRemoveAvailDoc(item)">
                <v-btn icon small class="mx-0">
                  <v-icon small color="pink">mdi-delete</v-icon>
                </v-btn>
                <template slot="message">This will remove Document</template>
              </confirmation-dialog>
            </v-row>
          </template>
        </v-data-table>
      </v-card>
    </v-col>
    <v-col>
      <h3 class="subtitle-1">Owner's Documents</h3>
      <v-card outlined>
        <v-data-table :items="ownerDocs" :headers="ownerDocsHeaders">
          <template v-slot:item.actions="{ item }">
            <v-row>
              <v-btn icon small class="mx-0">
                <v-icon small color="primary" @click="onDocEdit(item)"
                  >mdi-pencil</v-icon
                >
              </v-btn>
              <confirmation-dialog @onConfirm="onRemoveDoc(item)">
                <v-btn icon small class="mx-0">
                  <v-icon small color="pink">mdi-delete</v-icon>
                </v-btn>
                <template slot="message">This will remove Document</template>
              </confirmation-dialog>
            </v-row>
          </template>
        </v-data-table>
      </v-card>
      <document-type-dialog
        v-model="selectedDocType"
        :show="showDocumentTypeDialog"
        @close="showDocumentTypeDialog = false"
        @update="onDocUpdate"
      ></document-type-dialog>
      <document-builder
        :show="showDocumentBuilderDialog"
        @close="showDocumentBuilderDialog = false"
        v-model="selectedAvailDoc"
        is-public
        :domain="domain"
      />
      <v-row v-if="debug">
        <code>{{ selectedAvailDoc }}</code>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import gql from "graphql-tag";
import { removeGQLTypename } from "../../../../helpers";
import ConfirmationDialog from "../../shared/ConfirmationDialog.vue";
import DocumentTypeDialog from "./DocumentTypeDialog.vue";
import DocumentBuilder from "../documents/DocumentBuilder.vue";

const ALL_DOCS = gql`
  query allDocs {
    allAvailableDocuments {
      edges {
        node {
          id
          name
          type
          description
        }
      }
    }
  }
`;
const OWNER_DOCS = gql`
  query ownerDocs($domain: String) {
    documentTypes(domain: $domain) {
      edges {
        node {
          id
          name
          format
        }
      }
    }
  }
`;
const ADD_DOC = gql`
  mutation addDocToOwner($input: DocumentTypeInput!) {
    createDocumentType(input: $input) {
      id
      name
      format
    }
  }
`;
const REMOVE_DOC = gql`
  mutation removeOwnerDoc($id: ID!) {
    deleteDocumentType(id: $id) {
      id
    }
  }
`;
const REMOVE_AVAIL_DOC = gql`
  mutation removeAvailDoc($id: ID!) {
    deleteAvailableDocument(id: $id) {
      id
    }
  }
`;
const UPDATE_DOC = gql`
  mutation updateDocumentType($input: UpdateDocumentTypeInput!) {
    updateDocumentType(input: $input) {
      id
      name
      format
    }
  }
`;
const COPY_AVAIL_DOC = gql`
  mutation copyAvailableDocument($input: AvailableDocumentInput!) {
    copyAvailableDocument(input: $input) {
      id
      name
    }
  }
`;

export default {
  components: { ConfirmationDialog, DocumentTypeDialog, DocumentBuilder },
  name: "owner-documents",

  props: {
    value: Object,
    default: () => null,
  },

  data() {
    return {
      debug: false,
      allDocsHeaders: [
        {
          text: "Name",
          value: "name",
          name: "name",
        },
        {
          text: "Type",
          value: "type",
          name: "type",
        },
        {
          text: "Description",
          value: "description",
          name: "description",
        },
        {
          text: "Actions",
          value: "actions",
          name: "actions",
        },
      ],
      ownerDocsHeaders: [
        {
          text: "Name",
          value: "name",
          name: "name",
        },
        {
          text: "Format",
          value: "format",
          name: "format",
        },
        {
          text: "Actions",
          value: "actions",
          name: "actions",
        },
      ],
      showDocumentTypeDialog: false,
      selectedDocType: null,
      showDocumentBuilderDialog: false,
      selectedAvailDoc: null,
    };
  },

  computed: {
    allDocs() {
      return this.allAvailableDocuments
        ? this.allAvailableDocuments.edges.map(removeGQLTypename)
        : [];
    },
    ownerDocs() {
      return this.documentTypes
        ? this.documentTypes.edges.map(removeGQLTypename)
        : [];
    },
    domain() {
      return this.value ? this.value.domain : "";
    },
  },

  methods: {
    onCloneAvailDoc(item) {
      this.$apollo
        .mutate({
          mutation: COPY_AVAIL_DOC,
          variables: { input: { id: item.id } },
          refetchQueries: [
            {
              query: ALL_DOCS,
            },
          ],
        })
        .then((res) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Document Copied.",
          });
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    onRemoveAvailDoc(item) {
      this.$apollo
        .mutate({
          mutation: REMOVE_AVAIL_DOC,
          variables: { id: item.id },
          refetchQueries: [
            {
              query: ALL_DOCS,
            },
          ],
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Document Removed.",
          });
        })
        .catch(this.errorHandler);
    },
    onCreateDocument() {
      this.selectedAvailDoc = null;
      this.showDocumentBuilderDialog = true;
    },
    onAvailDocDetails(item) {
      this.selectedAvailDoc = { ...item };
      this.showDocumentBuilderDialog = true;
    },
    onDocEdit(item) {
      this.selectedDocType = { ...item };
      this.showDocumentTypeDialog = true;
    },
    onDocUpdate(payload) {
      console.log("updating doc", payload, this.selectedDocType);
      this.$apollo
        .mutate({
          mutation: UPDATE_DOC,
          variables: {
            input: {
              id: this.selectedDocType.id,
              name: payload.name,
              format: payload.format,
            },
          },
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Document Updated.",
          });
          this.showDocumentTypeDialog = false;
        })
        .catch(this.errorHandler);
    },
    onRemoveDoc(item) {
      this.$apollo
        .mutate({
          mutation: REMOVE_DOC,
          variables: { id: item.id },
          refetchQueries: [
            {
              query: OWNER_DOCS,
              variables: { domain: this.value.domain },
            },
          ],
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Document Removed.",
          });
        })
        .catch(this.errorHandler);
    },
    onAddDocument(item) {
      this.$apollo
        .mutate({
          mutation: ADD_DOC,
          variables: {
            input: {
              templateId: item.id,
              domain: this.value.domain,
            },
          },
          refetchQueries: [
            {
              query: OWNER_DOCS,
              variables: { domain: this.value.domain },
            },
          ],
        })
        .then(({ data }) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Document added.",
          });
        })
        .catch(this.errorHandler);
    },
    errorHandler(err) {
      const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
      this.$store.dispatch("showAppSnackbar", {
        msg: errorMsg,
        color: "error",
      });
    },
  },

  apollo: {
    allAvailableDocuments: {
      query: ALL_DOCS,
    },
    documentTypes: {
      query: OWNER_DOCS,
      variables() {
        return { domain: this.value.domain };
      },
      skip() {
        return !this.value;
      },
    },
  },
};
</script>

<style scoped>
.v-data-table td {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 100%;
  max-width: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/gt/admin/user-access-controls/OwnerDocuments.vue